import React, { useEffect, useState } from "react";
import { Backdrop, Box, Card, CardContent, Chip, Grid, Typography } from "@mui/material";
import Header from "../header/header";
import PDBResponse from "../../interfaces/pdbResponse";
import apiClient from "../../axiosClient";
import Client from "../../interfaces/client";
import { AppDispatch } from "../../store/store";
import { loadClients } from "../../store/slices/clientSlice";
import { useDispatch } from "react-redux";
import { apiCallWrapper, decodedJWT } from "../../utils/commonUtils";
import PDBService from "../../services/pdbService";
import { useNavigate } from "react-router-dom";
import Loader from "../baseComponents/loader";
import JWTTokenPayload from "../../interfaces/JWTTokenPayload";

const styles = {
    pageContainer: {
        height: '100vh',
        width: '100vw',
        m: 2
    },
    container: {
        m: 2,
        overflowY: 'auto'
    },
    gridContainer: {
        m: 3
    },
    cardWrapper: {
        height: '32vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        position: 'relative',
        overflowY: 'auto'
    },
    boldTextWithMargin: {
        fontWeight: 'bold',
        mt: -0.5
    },
    greyText: {
        color: '#708090'
    },
    chipGridContainer: {
        mt: 2,
    },
    boldText: {
        fontWeight: 'bold',
    },
    titleText: {
        ml: 3
    },
};

const PortfolioPage: React.FC = () => {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const token: JWTTokenPayload | false = decodedJWT();
    const [loading, setLoading] = useState<boolean>(false);
    const [pdbData, setPdbData] = useState<PDBResponse[]>([]);
    const [hoveringCardIndex, setHoveringCardIndex] = useState<number>(-1);

    const checkJWTTokenExpiration = () => {
		if (!token) {
            navigate('/login');
            return false;
        } else {
            const { exp } = token;
            const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
            if (exp < currentTime) {
                alert('Your Session has Expired \nPlease Login again');
                navigate('/login');
                return false;
            }
            return true;
        }
	};

    const loadPDBs = (clients: Client[]) => {
        const pdbClientMap: Record<string, string[]> = {};
        clients.forEach(val => {
            if (val.pdb_id) {
                if (pdbClientMap[val.pdb_id]) {
                    pdbClientMap[val.pdb_id].push(val.name);
                } else {
                    pdbClientMap[val.pdb_id] = [val.name];
                }
            }
        });
        const pdbDataArray: PDBResponse[] = Object.entries(pdbClientMap).map(val => {
            return { pdb_id: val[0], clients: val[1] }
        });
        setPdbData(pdbDataArray);
    };

    const fetchClients = async () => {
        try {
            setLoading(true);
            const response = await apiCallWrapper(() => apiClient.get('/api/v1/client'));
            if (response && response.status === 200) {
                const clientData = response.data as Client[];
                loadPDBs(clientData);
                dispatch(loadClients(clientData));
            }
        } catch (_) {} finally {
            setLoading(false);
        }
    };

    const switchPDB = async (pdbID: string) => {
        try {
            setLoading(true);
            const response = await PDBService.switchPDB(pdbID);
            if (response) {
                localStorage.setItem("token", response.token);
                navigate('/homepage');
            }
        } catch (_) {} finally {
            setLoading(false);
        }
    };

    const callAPIs = async () => {
        const loggedIn = checkJWTTokenExpiration();
        if (loggedIn) {
            fetchClients();
        }
    };

    useEffect(() => {
        callAPIs();
    }, []);

    return (
        <Box sx={styles.pageContainer}>
            <Header />
            <Box sx={styles.container}>
                {loading ? (
                    <Loader />
                ) : (
                    <>
                        <Typography sx={styles.titleText} variant="h6">Select Portfolio</Typography>
                        <Grid sx={styles.gridContainer} container spacing={2}>
                            {pdbData.map((pdb: PDBResponse, index: number) => {
                                return (
                                    <Grid
                                        onClick={() => switchPDB(pdb.pdb_id)}
                                        onMouseEnter={() => setHoveringCardIndex(index)}
                                        onMouseLeave={() => setHoveringCardIndex(-1)}
                                        size={3}
                                        sx={{ zIndex: hoveringCardIndex === index ? 10 : undefined }}
                                        key={index}
                                    >
                                        <Card sx={{
                                            ...styles.cardWrapper,
                                            transform: hoveringCardIndex === index ? 'scale(1.1)' : 'scale(1)',
                                            zIndex: hoveringCardIndex === index ? 20 : 1,
                                        }}>
                                            <CardContent>
                                                <Typography variant="h6" sx={styles.boldTextWithMargin}>Portfolio ID: {pdb.pdb_id}</Typography>
                                                <Typography variant="body2" sx={styles.greyText}>{pdb.clients.length} client{pdb.clients.length > 1 ? 's' : ''}</Typography>
                                                <Grid sx={styles.chipGridContainer} container spacing={1}>
                                                    {(hoveringCardIndex !== index ? pdb.clients.slice(0, 10) : pdb.clients).map((val: string, valIndex: number) => {
                                                        return (
                                                            <Chip key={valIndex} label={val} size="small" />
                                                        );
                                                    })}
                                                    {pdb.clients.length > 10 && hoveringCardIndex !== index && (
                                                        <Chip sx={styles.boldText} size="small" label={`+${pdb.clients.length - 10} more`} />
                                                    )}
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </>
                )}
                <Backdrop sx={{ zIndex: 1 }} open={hoveringCardIndex !== -1} />
            </Box>
        </Box>
    );
};

export default PortfolioPage;